import React, { useEffect, useState, useMemo } from 'react';
import LogoAlpop from '../../../../../../resources/imgs/logo-alpop.png';
import Button from '../../../../../../components/Button';
import { Icon } from 'semantic-ui-react';
import AlpopBanner from '../AlpopBanner';
import LenderBanner from '../LenderBanner';
import CapitalizationBanner from '../CaptalizationBanner';

interface OtherSolutionsBannerProps {
  setShowCapitalizationModal: (show: boolean) => void;
  lenderInsuranceEnabled?: boolean; // Se true, imobiliária já está habilitada, não exibir captura de leads
  allRefused?: boolean; // Se true, exibe Alpop e Capitalização
}

const OtherSolutionsBanner: React.FC<OtherSolutionsBannerProps> = ({
  setShowCapitalizationModal,
  lenderInsuranceEnabled = false,
  allRefused = false
}) => {
  // Se imobiliária está habilitada para Prestamista, não exibir a aba de captura de leads
  // Alpop e Capitalização só aparecem se allRefused for true
  const banners = useMemo(() => {
    const baseBanners: string[] = [];

    if (allRefused) {
      baseBanners.push('ALPOP', 'CAPTALIZATION');
    }

    if (!lenderInsuranceEnabled) {
      baseBanners.push('LENDER');
    }

    return baseBanners;
  }, [lenderInsuranceEnabled, allRefused]);

  const [bannerActive, setBannerActive] = useState<'ALPOP' | 'CAPTALIZATION' | 'LENDER'>(
    banners[0] as any || 'ALPOP'
  );

  const [index, setIndex] = useState(0);
  const [pause, setPause] = useState(false);

  useEffect(() => {
    if (banners.length <= 1) return;

    const timeout = setTimeout(() => {
      if (pause) return;
      setIndex(prev => (prev + 1) % banners.length);
      setBannerActive(
        banners[(index + 1) % banners.length] as 'ALPOP' | 'CAPTALIZATION' | 'LENDER'
      );
    }, 5000);

    return () => clearTimeout(timeout);
  }, [index, banners.length, banners, pause]);

  // Se não houver nada para exibir, retorna null
  if (banners.length === 0) return null;

  const itemHeight = `${100 / banners.length}%`;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '20px'
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 20,
          justifyContent: 'center',
          alignItems: 'center',
          color: '#59249B',
          fontWeight: 'bold',
          fontSize: '24px',
          paddingRight: '10px'
        }}
      >
        <div style={{ lineHeight: '26px' }}>
          Confira nossas
          <br />
          novas soluções
        </div>
        <div>
          <Icon name="chevron right" />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #C8ACFD',
          borderRight: 0,
          borderTopLeftRadius: '5px',
          borderBottomLeftRadius: '5px',
          width: '180px',
        }}
      >
        {allRefused && (
          <>
            <div
              style={{
                display: 'flex',
                width: '100%',
                height: itemHeight,
                justifyContent: 'center',
                alignItems: 'center',
                paddingLeft: '24px',
                paddingRight: '24px',
                borderBottom: banners.length > 1 ? '1px solid #C8ACFD' : 'none',
                borderTopLeftRadius: '5px',
                color: bannerActive === 'ALPOP' ? '#FFFFFF' : '#6B6F77',
                backgroundColor: bannerActive === 'ALPOP' ? '#59249B' : '#FFFFFF',
                fontWeight: bannerActive === 'ALPOP' ? 'bolder' : 'normal',
                cursor: 'pointer'
              }}
              onClick={() => setBannerActive('ALPOP')}
            >
              ALPOP
            </div>
            <div
              style={{
                display: 'flex',
                width: '100%',
                height: itemHeight,
                justifyContent: 'center',
                alignItems: 'center',
                paddingLeft: '24px',
                paddingRight: '24px',
                borderBottom: banners.length > (banners.indexOf('CAPTALIZATION') + 1) ? '1px solid #C8ACFD' : 'none',
                color: bannerActive === 'CAPTALIZATION' ? '#FFFFFF' : '#6B6F77',
                backgroundColor: bannerActive === 'CAPTALIZATION' ? '#59249B' : '#FFFFFF',
                fontWeight: bannerActive === 'CAPTALIZATION' ? 'bolder' : 'normal',
                cursor: 'pointer'
              }}
              onClick={() => setBannerActive('CAPTALIZATION')}
            >
              CAPITALIZAÇÃO
            </div>
          </>
        )}
        {!lenderInsuranceEnabled && (
          <div
            style={{
              display: 'flex',
              width: '100%',
              height: itemHeight,
              justifyContent: 'center',
              alignItems: 'center',
              paddingLeft: '24px',
              paddingRight: '24px',
              borderBottomLeftRadius: '5px',
              color: bannerActive === 'LENDER' ? '#FFFFFF' : '#6B6F77',
              backgroundColor: bannerActive === 'LENDER' ? '#59249B' : '#FFFFFF',
              fontWeight: bannerActive === 'LENDER' ? 'bolder' : 'normal',
              cursor: 'pointer'
            }}
            onClick={() => setBannerActive('LENDER')}
          >
            PRESTAMISTA
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px solid #C8ACFD',
          borderTopRightRadius: '5px',
          borderBottomRightRadius: '5px',
          paddingLeft: '20px',
          paddingRight: '20px',
          width: '50%',
          minHeight: '80px',
          gap: 30
        }}
      >
        {bannerActive === 'ALPOP' && <AlpopBanner />}
        {bannerActive === 'CAPTALIZATION' && (
          <CapitalizationBanner handleOnclick={() => setShowCapitalizationModal(true)} />
        )}
        {bannerActive === 'LENDER' && <LenderBanner setPause={setPause} />}
      </div>
    </div>
  );
};

export default OtherSolutionsBanner;
